import { useCallback, useState } from '@web-stories-wp/react';
import RetrySaveDialog from '../../dialog/retrySaveDialog';
import IgnoreSuggestionsDialog from '../../dialog/ignoreSuggestionsDialog';
import StoryNotFoundDialog from '../../dialog/storyNotFoundDialog';
import PagesValidationDialog from '../../dialog/pagesValidationDialog';
import StoryWasModifiedDialog from '../../dialog/storyWasModifiedDialog';
import { useStory } from '@src/web-stories-wp/story-editor/src/app';
import { useAuthorizationManager } from '@src/hooks/authorization';
import { useValidatePages } from '@hooks';
import { useStudioContext } from '@hooks/studioContext';
import { trackAppInsightsEvent } from '@src/initAppInsights';
import {
  useInspector,
  REFINE,
} from '@web-stories-wp/story-editor/src/components/inspector';

import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from '@web-stories-wp/design-system';
import { useConfig, useSpellCheck } from '@web-stories-wp/story-editor';

function SavePagesButton({
  isLoading,
  onSave,
  onSaveFinish,
  disabled,
  ...rest
}) {
  const { generatePages } = useStory(({ actions: { generatePages } }) => ({
    generatePages,
  }));

  const {
    state: { suggestions },
  } = useSpellCheck();

  const {
    actions: { setTab },
  } = useInspector();

  const { validatePages } = useValidatePages();
  const isAnimatedThumbnail = useConfig(({ animatedThumbnail }) =>
    Boolean(animatedThumbnail)
  );

  const [showIgnoreSuggestionsDialog, setShowIgnoreSuggestionsDialog] =
    useState(false);
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [showEntityNotFoundDialog, setShowEntityNotFoundDialog] =
    useState(false);

  const [validationResult, setValidationResult] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showStoryWasModified, setShowStoryWasModified] = useState(false);
  const studioContext = useStudioContext();
  const { authContext } = useAuthorizationManager();

  const save = useCallback(
    (addNewPagesAtTheEnd) => {
      trackAppInsightsEvent('Studio: Save Attempt Started', {
        addNewPagesAtTheEnd: Boolean(addNewPagesAtTheEnd),
      });
      onSave?.();

      const validation = isAnimatedThumbnail ? [] : validatePages();
      trackAppInsightsEvent('Studio: Save Validation Completed', {
        validationErrorCount: validation.length,
      });

      if (validation.length > 0) {
        setValidationResult(validation);
        setShowValidationModal(true);
        onSaveFinish?.();
        return;
      }

      return generatePages({ addNewPagesAtTheEnd })
        .then((saveResult) => {
          let storyChangedByOtherUser = false;
          if (saveResult) {
            storyChangedByOtherUser = saveResult.storyChangedByOtherUser;
          }

          if (storyChangedByOtherUser) {
            trackAppInsightsEvent('Studio: Save Blocked By Modified Story');
            setShowStoryWasModified(true);
            onSaveFinish?.();
            return;
          }

          onSaveFinish?.();
          trackAppInsightsEvent('Studio: Redirect To CMS Started');
          authContext.redirectToCmsEditForm(studioContext);
          return;
        })
        .catch((e) => {
          onSaveFinish?.();
          if (e?.name === 'LottieTextRowsError') {
            setValidationResult([
              {
                kind: 'lottie-copy',
                message: e.message,
                pageNumber: e.pageNumber,
                element: {},
              },
            ]);
            setShowValidationModal(true);
            return;
          }

          const response = e?.response;

          trackAppInsightsEvent('Studio: Save Attempt Failed', {
            errorName: e?.name || 'Error',
            ...(response?.status ? { responseStatus: response.status } : {}),
          });
          console.error(response?.data?.error?.details || e);

          if (response?.status === 404) {
            setShowEntityNotFoundDialog(true);
            return;
          }

          setShowRetryDialog(true);
        });
    },
    [
      authContext,
      onSave,
      validatePages,
      generatePages,
      isAnimatedThumbnail,
      onSaveFinish,
      studioContext,
    ]
  );

  const retrySave = useCallback(() => {
    setShowRetryDialog(false);
    save();
  }, [save]);

  const redirectToCms = useCallback(() => {
    authContext.redirectToCms();
  }, [authContext]);

  const closeValidationModal = useCallback(() => {
    setShowValidationModal(false);
  }, []);

  const saveModifiedStory = useCallback(() => {
    save(true);
    setShowStoryWasModified(false);
  }, [save]);

  const showVlidationDialog =
    showValidationModal && validationResult.length > 0;
  return (
    <>
      <Button
        {...rest}
        isLoading={isLoading}
        variant={BUTTON_VARIANTS.RECTANGLE}
        type={BUTTON_TYPES.PRIMARY}
        size={BUTTON_SIZES.SMALL}
        onClick={() => {
          trackAppInsightsEvent('Studio: Save Clicked', {
            suggestionsCount: suggestions.length,
          });

          if (!isAnimatedThumbnail && suggestions.length > 0) {
            setShowIgnoreSuggestionsDialog(true);
          } else {
            save(false);
          }
        }}
        disabled={disabled}
      >
        Save
      </Button>
      <RetrySaveDialog
        isOpen={showRetryDialog}
        onClose={() => setShowRetryDialog(false)}
        onRetryClick={retrySave}
      />
      <StoryNotFoundDialog
        isOpen={showEntityNotFoundDialog}
        onClose={() => setShowEntityNotFoundDialog(false)}
        onRedirectClick={redirectToCms}
      />
      <StoryWasModifiedDialog
        isOpen={showStoryWasModified}
        onClose={() => setShowStoryWasModified(false)}
        onClick={saveModifiedStory}
      />
      <IgnoreSuggestionsDialog
        isLoading={isLoading}
        isOpen={showIgnoreSuggestionsDialog}
        onClose={() => {
          setShowIgnoreSuggestionsDialog(false);
          setTab(REFINE);
        }}
        onIgnoreSuggestions={() => save(false)}
      />

      {showVlidationDialog && (
        <PagesValidationDialog
          isOpen={showVlidationDialog}
          onClose={closeValidationModal}
          onClick={closeValidationModal}
          validationResult={validationResult}
        />
      )}
    </>
  );
}

export default SavePagesButton;
